import React from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from 'common/components/Button';
import { Table, TableColumn } from 'common/components/Table';
import { TenantsSectionProps, Tenant } from './TenantsSection.types';
import styles from './TenantsSection.module.scss';
import { Chip, ChipColor } from 'common/components/Chip';

const STATUS_MAP = {
    ACTIVE: { label: 'Active', color: 'success' },
    INACTIVE: { label: 'Inactive', color: 'error' },
} as const;

export const TenantsSection: React.FC<TenantsSectionProps> = ({
    tenants,
    selectedTenant,
    onTenantSelect,
    onTenantToggleStatus,
    onTenantDelete,
    emptyText = 'No tenants found',
}) => {
    const getStatusInfo = (status?: string) => {
        return STATUS_MAP[status as keyof typeof STATUS_MAP] || { label: status || 'Unknown', color: 'info' };
    };

    const columns: TableColumn<Tenant>[] = [
        {
            key: 'id',
            title: 'Tenant ID',
            dataIndex: 'id',
        },
        {
            key: 'name',
            title: 'Name',
            dataIndex: 'name',
        },
        {
            key: 'status',
            title: 'Status',
            render: (_, record) => {
                const statusInfo = getStatusInfo(record.config?.status);
                return (
                    <Chip variant='filled' color={statusInfo.color as ChipColor} size='sm'>
                        {statusInfo.label}
                    </Chip>
                );
            },
        },
        {
            key: 'actions',
            title: 'Actions',
            render: (_, record) => (
                <div className={styles.actionsCell}>
                    <Button
                        variant='outline-secondary'
                        size='sm'
                        onClick={(e) => {
                            e.stopPropagation();
                            onTenantToggleStatus(record);
                        }}
                    >
                        {record.config?.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                    </Button>
                    <Button
                        variant='outline-secondary'
                        size='sm'
                        icon={Trash2}
                        iconOnly
                        onClick={(e) => onTenantDelete(record, e)}
                        title='Delete tenant'
                    />
                </div>
            ),
        },
    ];

    return (
        <div className={styles.tenantsSection}>
            <h2>Tenants</h2>
            <Table
                data={tenants}
                columns={columns}
                rowKey='id'
                onRowClick={(tenant) => onTenantSelect(tenant)}
                rowClassName={(tenant) => (selectedTenant?.id === tenant.id ? styles.selected : '')}
                hoverable
                variant='secondary'
                emptyText={emptyText}
                className={styles.tenantsTableWrapper}
            />
        </div>
    );
};
